import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock } from "lucide-react";
import { Link } from "react-router-dom";

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [eod, setEod] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const url = filter === "all" ? "/analyst-notes" : `/analyst-notes?commodity=${filter}`;
    const [n, c, e] = await Promise.all([api.get(url), api.get("/commodities"), api.get("/eod")]);
    setNotes(n.data); setCommodities(c.data);
    setEod(filter === "all" ? e.data : e.data.filter(x => x.commodity_code === filter));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Research</div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Analyst notes & EOD recaps</h1>
          <p className="mt-1 text-sm text-muted-foreground">Weekly analyst reports plus daily AI-written EOD recaps per commodity.</p>
        </div>
        <div className="w-56">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="notes-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All commodities</SelectItem>
              {commodities.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {eod.length > 0 && (
        <section>
          <div className="mb-3 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">EOD Recaps · today</div>
          <div className="grid gap-3 md:grid-cols-2">
            {eod.slice(0, 6).map(r => (
              <div key={r.id} data-testid={`eod-feed-${r.commodity_code}`} className="rounded-md border border-border/60 bg-card/60 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="uppercase text-[10px]">{r.commodity_code}</Badge>
                    <span className={`text-[10px] font-mono uppercase tracking-widest ${r.sentiment==="bullish"?"text-[hsl(var(--signal-buy))]":r.sentiment==="bearish"?"text-[hsl(var(--signal-wait))]":"text-[hsl(var(--signal-hold))]"}`}>{r.sentiment}</span>
                  </div>
                  {r.locked && <Badge variant="outline" className="uppercase text-[10px]"><Lock className="mr-1 h-3 w-3" />Pro</Badge>}
                </div>
                <div className="mt-2 font-display text-sm font-semibold">{r.headline}</div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{r.one_liner}</p>
                {r.locked && (
                  <Link to="/app/pricing" className="mt-2 inline-block text-[10px] font-mono uppercase tracking-widest text-primary hover:underline">Unlock full recap →</Link>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <div>
        <div className="mb-3 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Weekly analyst notes</div>
        <div className="grid gap-4 md:grid-cols-2">
          {notes.map(n => (
          <Card key={n.id} data-testid={`note-${n.id}`} className="border-border/60 bg-card/60">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="uppercase text-[10px]">{n.commodity_code}</Badge>
                  <span className={`text-[10px] font-mono uppercase tracking-widest ${n.sentiment==="bullish"?"text-[hsl(var(--signal-buy))]":n.sentiment==="bearish"?"text-[hsl(var(--signal-wait))]":"text-[hsl(var(--signal-hold))]"}`}>{n.sentiment}</span>
                </div>
                {n.locked ? (
                  <Badge variant="outline" className="uppercase text-[10px]"><Lock className="mr-1 h-3 w-3" />{n.tier_required}</Badge>
                ) : (
                  <span className="text-[10px] font-mono text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</span>
                )}
              </div>
              <div className="mt-3 font-display text-lg font-semibold">{n.title}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{n.author}</div>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{n.body}</p>
              {n.locked && (
                <Link to="/app/pricing" className="mt-3 inline-block text-xs text-primary hover:underline">Unlock full note →</Link>
              )}
            </CardContent>
          </Card>
        ))}
        </div>
      </div>
    </div>
  );
}
